"use client";

import { Fragment, type ReactNode, useState } from "react";
import { useTranslations } from "use-intl";

import { GoogleAuthButton } from "@/components/GoogleAuthButton";
import { PhoneOtpAuth } from "@/components/phone-otp-auth";
import { TelegramAuthButton } from "@/components/TelegramAuthButton";
import { useViewer } from "@/hooks/use-viewer";

import {
  getAuthAlternativeMethodOrder,
  type AuthAlternativeMethod,
} from "./getAuthAlternativeMethodOrder";

interface AuthAlternativeMethodsProps {
  mode: "signin" | "signup";
  onWhatsAppExpandedChange?: (expanded: boolean) => void;
  redirectAfterLogin?: string;
}

/** Renders full-page authentication methods that live outside the email form. */
export function AuthAlternativeMethods({
  mode,
  onWhatsAppExpandedChange,
  redirectAfterLogin,
}: AuthAlternativeMethodsProps) {
  const t = useTranslations("globals.auth");
  const viewer = useViewer();
  const [whatsappExpanded, setWhatsAppExpanded] = useState(false);

  function handleWhatsAppExpandedChange(expanded: boolean) {
    setWhatsAppExpanded(expanded);
    onWhatsAppExpandedChange?.(expanded);
  }

  const actions: Record<AuthAlternativeMethod, ReactNode> = {
    google: <GoogleAuthButton mode={mode} redirectAfterLogin={redirectAfterLogin} />,
    telegram: viewer.capabilities.telegramAuthAvailable ? (
      <TelegramAuthButton mode={mode} redirectAfterLogin={redirectAfterLogin} />
    ) : null,
    whatsapp: (
      <PhoneOtpAuth
        mode={mode}
        redirectAfterLogin={redirectAfterLogin}
        onExpandedChange={handleWhatsAppExpandedChange}
      />
    ),
  };
  const methods: readonly AuthAlternativeMethod[] = whatsappExpanded
    ? ["whatsapp"]
    : getAuthAlternativeMethodOrder(mode);

  return (
    <div className="space-y-3">
      {methods.map((method) => (
        <Fragment key={method}>{actions[method]}</Fragment>
      ))}

      {!whatsappExpanded && (
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">{t("orContinueWith")}</span>
          </div>
        </div>
      )}
    </div>
  );
}
